import React, { useEffect, useMemo, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import { CapitalizeAllWords, convertToPersian } from "../../Functions/TextTransform";

export function getWaitlistColorProducts(designProducts, activeProduct) {
  return getWaitlistColorProductsByColorId(designProducts, activeProduct?.["ColorId"]);
}

export function getWaitlistColorProductsByColorId(designProducts, colorId) {
  if (colorId == null || colorId === "" || !designProducts) {
    return [];
  }
  return designProducts[colorId] || designProducts[String(colorId)] || [];
}

export function resolveWaitlistVariantProduct(colorProducts, sizeEnName, liningTypeId) {
  if (!colorProducts?.length) {
    return null;
  }
  if (sizeEnName && liningTypeId != null && liningTypeId !== "") {
    const exact = colorProducts.find(
      (p) =>
        p["ProductSizeEnName"] === sizeEnName &&
        String(p["LiningTypeId"]) === String(liningTypeId)
    );
    if (exact) {
      return exact;
    }
  }
  if (sizeEnName) {
    const bySize = colorProducts.find((p) => p["ProductSizeEnName"] === sizeEnName);
    if (bySize) {
      return bySize;
    }
  }
  if (liningTypeId != null && liningTypeId !== "") {
    const byLining = colorProducts.find(
      (p) => String(p["LiningTypeId"]) === String(liningTypeId)
    );
    if (byLining) {
      return byLining;
    }
  }
  return colorProducts[0];
}

export default function WaitlistModalOneCurtain({
  pageLanguage,
  modals,
  modalHandleClose,
  t,
  activeProduct,
  designProducts,
  selectedSize,
  isSizeSelected,
  isLiningSelected,
  hasSingleSizeForColor,
  hasSingleLiningType,
  waitlistEmail,
  setWaitlistEmail,
  waitlistEmailError,
  setWaitlistEmailError,
  waitlistLoading,
  onSubmitWaitlist,
  onWaitlistProductChange,
}) {
  const [modalColorId, setModalColorId] = useState("");
  const [modalSize, setModalSize] = useState("");
  const [modalLining, setModalLining] = useState("");
  const [selectionError, setSelectionError] = useState("");

  const colorProducts = useMemo(() => {
    const colorId = modalColorId || activeProduct?.["ColorId"];
    return getWaitlistColorProductsByColorId(designProducts, colorId);
  }, [designProducts, modalColorId, activeProduct]);

  const waitlistModalWasOpenRef = useRef(false);

  const getInitialModalSize = () => {
    if (isSizeSelected || hasSingleSizeForColor) {
      return selectedSize || activeProduct["ProductSizeEnName"] || "";
    }
    return "";
  };

  const getInitialModalLining = () => {
    if (isLiningSelected || hasSingleLiningType) {
      return activeProduct["LiningTypeId"] != null
        ? String(activeProduct["LiningTypeId"])
        : "";
    }
    return "";
  };

  useEffect(() => {
    const isOpen = modals["waitlist"] === true;
    if (isOpen && !waitlistModalWasOpenRef.current) {
      setSelectionError("");
      const nextColorId =
        activeProduct["ColorId"] != null ? String(activeProduct["ColorId"]) : "";
      const nextSize = getInitialModalSize();
      const nextLining = getInitialModalLining();
      setModalColorId(nextColorId);
      setModalSize(nextSize);
      setModalLining(nextLining);
      const products = getWaitlistColorProductsByColorId(designProducts, nextColorId);
      const product = resolveWaitlistVariantProduct(products, nextSize, nextLining);
      if (product && onWaitlistProductChange) {
        onWaitlistProductChange(product["ProductId"]);
      } else if (onWaitlistProductChange && activeProduct["ProductId"]) {
        onWaitlistProductChange(activeProduct["ProductId"]);
      }
    }
    waitlistModalWasOpenRef.current = isOpen;
  }, [modals["waitlist"]]);

  const handleNotifyClick = () => {
    setSelectionError("");
    if (setWaitlistEmailError) {
      setWaitlistEmailError("");
    }

    const hasSize = Boolean(modalSize);
    const hasLining = Boolean(modalLining);

    if (!hasSize && !hasLining) {
      setSelectionError(t("Choose a size and lining so we know which one to watch for."));
      return;
    }
    if (!hasSize && hasLining) {
      setSelectionError(t("Choose a size so we know which one to watch for."));
      return;
    }
    if (hasSize && !hasLining) {
      setSelectionError(t("Choose a lining so we know which one to watch for."));
      return;
    }

    onSubmitWaitlist();
  };

  const applyModalVariant = (products, nextSize, nextLining) => {
    if (!products?.length) {
      return null;
    }
    if (!nextSize && !nextLining) {
      if (onWaitlistProductChange) {
        onWaitlistProductChange(products[0]["ProductId"]);
      }
      return products[0];
    }
    const product = resolveWaitlistVariantProduct(products, nextSize, nextLining);
    if (product && onWaitlistProductChange) {
      onWaitlistProductChange(product["ProductId"]);
    }
    return product;
  };

  const handleModalColorChange = (nextColorId) => {
    setModalColorId(nextColorId);
    const nextColorProducts = getWaitlistColorProductsByColorId(designProducts, nextColorId);
    if (!nextColorProducts.length) {
      return;
    }

    let nextSize = modalSize;
    if (nextSize && !nextColorProducts.some((p) => p["ProductSizeEnName"] === nextSize)) {
      nextSize = "";
    }

    let nextLining = modalLining;
    if (
      nextLining &&
      !nextColorProducts.some((p) => String(p["LiningTypeId"]) === String(nextLining))
    ) {
      nextLining = "";
    }

    setModalSize(nextSize);
    setModalLining(nextLining);
    applyModalVariant(nextColorProducts, nextSize, nextLining);
  };
  const colorOptions = useMemo(() => {
    if (!designProducts || !Object.keys(designProducts).length) {
      return [];
    }
    const colorMap = new Map();
    Object.keys(designProducts).forEach((key) => {
      const product = designProducts[key][0];
      const id = String(product["ColorId"]);
      if (!colorMap.has(id)) {
        colorMap.set(id, {
          id,
          label:
            pageLanguage === "fa"
              ? convertToPersian(product["ColorName"])
              : CapitalizeAllWords(product["ColorEnName"]),
        });
      }
    });
    return [...colorMap.values()].sort((a, b) => a.label.localeCompare(b.label));
  }, [designProducts, pageLanguage]);

  const { sizeOptions, liningOptions } = useMemo(() => {
    const empty = { sizeOptions: [], liningOptions: [] };
    if (!colorProducts.length) {
      return empty;
    }
    const sizeMap = new Map();
    const liningMap = new Map();
    colorProducts.forEach((p) => {
      if (p["ProductSizeEnName"] && !sizeMap.has(p["ProductSizeEnName"])) {
        sizeMap.set(p["ProductSizeEnName"], {
          value: p["ProductSizeEnName"],
          label:
            pageLanguage === "fa" ? convertToPersian(p["ProductSizeName"]) : p["ProductSizeEnName"],
        });
      }
      if (p["LiningTypeId"] != null && !liningMap.has(p["LiningTypeId"])) {
        liningMap.set(p["LiningTypeId"], {
          value: String(p["LiningTypeId"]),
          label:
            pageLanguage === "fa" ? p["LiningTypeName"] : p["LiningTypeEnName"],
        });
      }
    });
    return {
      sizeOptions: [...sizeMap.values()],
      liningOptions: [...liningMap.values()].sort((a, b) => Number(a.value) - Number(b.value)),
    };
  }, [colorProducts, pageLanguage]);

  const selectedColorId =
    modalColorId || (activeProduct["ColorId"] != null ? String(activeProduct["ColorId"]) : "");
  const selectedSizeValue = modalSize;
  const selectedLiningId = modalLining;

  const productTitle =
    pageLanguage === "fa"
      ? `${activeProduct["ProductName"] || ""} ${activeProduct["SewingModelName"] || ""}`.trim()
      : `${activeProduct["ProductENName"] || ""} ${activeProduct["SewingModelEnName"] || ""}`.trim();

  return (
    <Modal
      backdrop="static"
      keyboard={false}
      dialogClassName={`waitlist_modal noMount_modal mediumSizeModal scroll_on ${
        pageLanguage === "fa" ? "font_farsi" : "font_en"
      }`}
      show={modals["waitlist"] === undefined ? false : modals["waitlist"]}
      onHide={() => modalHandleClose("waitlist")}
      scrollable={true}
    >
      <Modal.Header closeButton></Modal.Header>
      <Modal.Body>
        <p className="measurementsHelp_modal_title measurementsHelp_modal_title_desktop">
          {t("NOTIFY ME WHEN AVAILABLE")}
        </p>
        <p className="measurementsHelp_modal_title measurementsHelp_modal_title_mobile">
          {t("NOTIFY ME WHEN AVAILABLE")}
        </p>
        <p className="waitlist_modal_sub">{t("We'll notify you when this product is back in stock.")}</p>
        {productTitle ? <p className="waitlist_modal_product">{productTitle}</p> : null}
        <div className="waitlist_modal_form">
          <select
            className="form-control checkout_native_select"
            value={selectedColorId}
            aria-label={t("Color")}
            onChange={(e) => handleModalColorChange(e.target.value)}
          >
            {colorOptions.map((opt) => (
              <option key={opt.id} value={opt.id}>
                {opt.label}
              </option>
            ))}
          </select>
          <div className="waitlist_modal_form_row">
            <select
              className="form-control checkout_native_select"
              value={selectedSizeValue}
              aria-label={t("Size")}
              onChange={(e) => {
                const nextSize = e.target.value;
                setModalSize(nextSize);
                if (selectionError) {
                  setSelectionError("");
                }
                applyModalVariant(colorProducts, nextSize, modalLining);
              }}
            >
              <option value="">{t("Size")}</option>
              {sizeOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <select
              className="form-control checkout_native_select"
              value={selectedLiningId}
              aria-label={t("Lining")}
              onChange={(e) => {
                const nextLining = e.target.value;
                setModalLining(nextLining);
                if (selectionError) {
                  setSelectionError("");
                }
                applyModalVariant(colorProducts, modalSize, nextLining);
              }}
            >
              <option value="">{t("Lining")}</option>
              {liningOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          <input
            type="email"
            className="form-control"
            placeholder={t("Email address")}
            value={waitlistEmail}
            onChange={(e) => {
              setWaitlistEmail(e.target.value);
              if (waitlistEmailError && setWaitlistEmailError) {
                setWaitlistEmailError("");
              }
            }}
            autoComplete="email"
          />
          <div className="checkbox_style waitlist_modal_checkbox">
            <input type="checkbox" id="waitlist_modal_news" defaultChecked={false} />
            <label htmlFor="waitlist_modal_news" className="checkbox_label">
              <img
                className="checkbox_label_img checkmark1 img-fluid"
                src={require("../../Images/public/checkmark1_checkbox.png")}
                alt=""
              />
            </label>
            <span className="checkbox_text">{t("Sign me up for home news & offers")}</span>
          </div>
         <div className="waitlist_modal_errors_box">
         {selectionError ? (
            <p className="waitlist_modal_selection_err">{selectionError}</p>
          ) : null}
          {waitlistEmailError ? (
            <p className="waitlist_modal_err join_waitlist_message join_waitlist_message_error">
              {waitlistEmailError}
            </p>
          ) : null}
         </div>
          <div className="text_center waitlist_modal_submit">
            <button
              type="button"
              className="btn btn-new-dark"
              disabled={waitlistLoading}
              onClick={handleNotifyClick}
            >
              {waitlistLoading ? t("ADDING...") : t("Notify me when available")}
            </button>
          </div>
          <p className="waitlist_modal_fine">{t("We don't share your information with others.")}</p>
        </div>
      </Modal.Body>
    </Modal>
  );
}
